import { useEffectEvent } from "../../lib/use-effect-event";
"use client";

// @ubm/ui/components/carousel — LE TRANSPORT de piste du Carousel, factorisé (lot U9, mandat Root additif, 05/10/2026).
// Code de geste du Carousel canonique (UIArc arc-library@7238f981, MIT © 2026 Elia Kuratli) DÉPLACÉ ici sans changement de
// comportement : seuil, verrou d'axe, élan projeté, lancer d'au moins une diapositive, élastique aux bouts, molette latérale,
// rattrapage d'une piste en mouvement, tap sur une voisine. Le Carousel l'utilise par défaut ; CoverFlow l'utilise avec une
// autre présentation. Spécialisations OPTIONNELLES et déclarées (défaut : désactivées, comportement du Carousel inchangé) :
//   · `reducedStepping` : en mouvement réduit, un glisser au-delà de 40 px ou un balayage de molette avance d'UNE diapositive,
//     sans suivi 1:1 (contrat public de Cover flow) ;
//   · `onPass` : notifie la diapositive qui passe devant PENDANT un glisser ou une molette (contrat public de Cover flow) ;
//   · `authoritative` : en mode contrôlé, si le parent n'adopte pas l'indice demandé, la piste revient à l'indice du parent
//     (aucune désynchronisation visuelle / ARIA). Le Carousel l'emploie aussi depuis Fidelity Médias (06/10/2026).
// `pointercancel` (Fidelity Médias) : retour à l'indice retenu, sans choix ni rappel (voir `finish`).
import { useEffect, useRef, useState } from "react";
import type { MutableRefObject, PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent, RefObject } from "react";
import { animate, useMotionValue } from "motion/react";
import type { AnimationPlaybackControls, MotionValue } from "motion/react";
import { motionTokens } from "../../lib/motion-tokens";

/** Movement before a press is read as a drag or handed to the page as a vertical scroll. */
const SLOP = 6;
/** Release speed in px/s that always moves at least one slide. */
const FLICK = 360;
/** Mouvement réduit + `reducedStepping` : distance de glisser qui avance d'une diapositive. */
const REDUCED_STEP = 40;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
/** Momentum projection with a paging deceleration rate: where a flick would come to rest. */
const project = (velocity: number, rate = .995) => velocity / 1000 * rate / (1 - rate);
/** Past an edge every pixel costs more, like pulling against elastic. */
const rubber = (overshoot: number, dimension: number) => overshoot * dimension * .55 / (dimension + .55 * overshoot);
const unrubber = (distance: number, dimension: number) => distance * dimension / (.55 * Math.max(1, dimension - distance));

function velocityOf(samples: [number, number][], now: number) {
  const recent = samples.filter(([time]) => now - time <= 90);
  const first = recent[0], last = recent[recent.length - 1];
  // Moins de deux échantillons récents : aucune vitesse mesurable (même règle qu'avant).
  if (recent.length < 2 || !first || !last || now - last[0] > 50) return 0;
  const [firstTime, firstX] = first, [lastTime, lastX] = last;
  return lastTime > firstTime ? (lastX - firstX) / ((lastTime - firstTime) / 1000) : 0;
}

type Drag = { pointer: number; startX: number; startY: number; origin: number; from: number; locked: "x" | "y" | null; caught: boolean; samples: [number, number][] };

export interface SlideTransportOptions {
  count: number;
  /** Indice affiché (contrôlé ou propre au consommateur). */
  index: number;
  /** Un autre indice est demandé : le consommateur l'adopte (non contrôlé) et notifie. `announce` : le changer s'annonce. */
  onSelect: (next: number, announce: boolean) => void;
  reduced: boolean;
  /** Pas entre deux diapositives et largeur visible (px), mesurés par le consommateur. */
  metrics: MutableRefObject<{ step: number; width: number }>;
  /** Élément qui reçoit la molette latérale (écouteur de l'élément, non passif). */
  viewportRef: RefObject<HTMLElement | null>;
  /** Tap : indice de la diapositive sous le pointeur, ou `null`. */
  hitTest?: (clientX: number) => number | null;
  /** Position continue (en diapositives) à chaque changement de la piste. */
  onPosition?: (position: number) => void;
  reducedStepping?: boolean;
  onPass?: (index: number) => void;
  /** Mode contrôlé : `true` si l'indice vient du parent. Avec `authoritative`, un refus du parent ramène la piste. */
  controlled?: boolean;
  authoritative?: boolean;
  /** Gestes refusés (hôte désengagé par exemple). */
  disabled?: boolean;
}

export interface SlideTransport {
  /** Translation de la piste (px) : `-position * step`. */
  x: MotionValue<number>;
  /** Position continue de la piste, en diapositives. */
  progress: MotionValue<number>;
  /** Indice visé par la piste. */
  target: MutableRefObject<number>;
  travel: MutableRefObject<AnimationPlaybackControls | null>;
  drag: MutableRefObject<Drag | null>;
  dragging: boolean;
  /** Ressort vers une diapositive depuis où que soit la piste, vitesse comprise. */
  glide: (to: number, velocity?: number) => void;
  select: (to: number, options?: { velocity?: number; announce?: boolean }) => void;
  handlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
    onClickCapture: (event: ReactMouseEvent<HTMLElement>) => void;
  };
}

/** Le transport de piste unique (Carousel, CoverFlow). */
export function useSlideTransport({ count, index, onSelect, reduced, metrics, viewportRef, hitTest, onPosition, reducedStepping = false, onPass, controlled = false, authoritative = false, disabled = false }: SlideTransportOptions): SlideTransport {
  const last = Math.max(0, count - 1);
  const x = useMotionValue(0);
  const progress = useMotionValue(index);
  const target = useRef(index);
  const travel = useRef<AnimationPlaybackControls | null>(null);
  const drag = useRef<Drag | null>(null);
  const wheel = useRef({ active: false, raw: 0, timer: 0, stepped: false });
  const swallowClick = useRef(false);
  const passed = useRef(index);
  // Indice retenu par le consommateur au moment où un geste se termine (après un `onPass` adopté, il a déjà changé).
  const retained = useRef(index);
  retained.current = index;
  const [dragging, setDragging] = useState(false);
  const [requested, setRequested] = useState(0);
  const stepping = reduced && reducedStepping;
  // Lu au moment où un minuteur ou un relâchement arrive : un geste fini sous hôte désengagé ne valide rien.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const bounds = () => ({ min: -last * metrics.current.step, max: 0 });
  const band = (raw: number) => {
    const { min, max } = bounds(), size = metrics.current.width || 1;
    return raw > max ? max + rubber(raw - max, size) : raw < min ? min - rubber(min - raw, size) : raw;
  };
  const unband = (shown: number) => {
    const { min, max } = bounds(), size = metrics.current.width || 1;
    return shown > max ? max + unrubber(shown - max, size) : shown < min ? min - unrubber(min - shown, size) : shown;
  };

  function glide(to: number, velocity = 0) {
    const { step } = metrics.current;
    travel.current?.stop();
    travel.current = null;
    if (!step) return;
    if (reduced) { x.jump(-to * step); return; }
    travel.current = animate(x, -to * step, { ...motionTokens.spring.smooth, velocity });
  }

  function select(to: number, { velocity = 0, announce = true }: { velocity?: number; announce?: boolean } = {}) {
    const next = clamp(to, 0, last);
    target.current = next;
    glide(next, velocity);
    if (next === index) return;
    onSelect(next, announce);
    if (controlled && authoritative) setRequested((n) => n + 1);
  }

  // Parent contrôlé et autoritaire : s'il n'a pas adopté l'indice demandé, la piste revient au sien.
  useEffect(() => {
    if (!authoritative || !controlled || drag.current || !requested) return;
    if (target.current !== index) { target.current = index; glide(index); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, index, authoritative, controlled]);

  // Progress is the continuous slide position that the presentation reads every frame.
  const position = useEffectEvent((value: number) => {
    const { step } = metrics.current;
    if (!step) return;
    const at = -value / step;
    progress.set(at);
    onPosition?.(at);
    if (onPass && (drag.current?.locked === "x" || wheel.current.active)) {
      const near = clamp(Math.round(at), 0, last);
      if (near !== passed.current) { passed.current = near; onPass(near); }
    }
  });
  useEffect(() => x.on("change", (value) => position(value)), [x]);
  useEffect(() => { passed.current = index; }, [index]);

  // A horizontal trackpad swipe moves the track 1:1 and settles on the nearest slide once the swipe, including its momentum, ends.
  const onWheel = useEffectEvent((event: WheelEvent) => {
    const state = wheel.current;
    if (disabled || count < 2 || drag.current || !metrics.current.step) return;
    if (!state.active && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    event.preventDefault();
    if (stepping) {
      // Mouvement réduit + pas à pas : un balayage = une diapositive, le reste du même balayage est ignoré.
      if (!state.active) { state.active = true; select(target.current + (event.deltaX > 0 ? 1 : -1)); }
      window.clearTimeout(state.timer);
      state.timer = window.setTimeout(() => { state.active = false; }, 260);
      return;
    }
    if (!state.active) { state.active = true; travel.current?.stop(); travel.current = null; state.raw = unband(x.get()); }
    state.raw -= event.deltaX * (event.deltaMode === 1 ? 16 : 1);
    x.set(band(state.raw));
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => {
      state.active = false;
      if (disabledRef.current) { glide(target.current); return; }
      select(Math.round(-x.get() / metrics.current.step), { velocity: x.getVelocity() });
    }, 120);
  });
  useEffect(() => {
    const viewport = viewportRef.current, state = wheel.current;
    if (!viewport) return;
    const listener = (event: WheelEvent) => onWheel(event);
    viewport.addEventListener("wheel", listener, { passive: false });
    return () => { viewport.removeEventListener("wheel", listener); window.clearTimeout(state.timer); };
  }, [viewportRef]);

  function onPointerDown(event: ReactPointerEvent<HTMLElement>) {
    if (disabled || count < 2 || drag.current || !metrics.current.step || (event.pointerType === "mouse" && event.button !== 0)) return;
    swallowClick.current = false;
    // Touching a visibly moving track catches it where it is, the way a hand stops a spinning wheel. A settling tail is left alone so a tap still selects.
    const caught = travel.current !== null && x.isAnimating() && Math.abs(x.get() + target.current * metrics.current.step) > 12;
    if (caught) { travel.current?.stop(); travel.current = null; }
    drag.current = { pointer: event.pointerId, startX: event.clientX, startY: event.clientY, origin: 0, from: target.current, locked: null, caught, samples: [] };
  }

  function onPointerMove(event: ReactPointerEvent<HTMLElement>) {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    if (!current.locked) {
      const dx = event.clientX - current.startX, dy = event.clientY - current.startY;
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
      if (Math.abs(dy) > Math.abs(dx)) { current.locked = "y"; return; }
      // The slop is consumed rather than applied, so the track starts moving from the finger without a jump.
      current.locked = "x";
      travel.current?.stop();
      travel.current = null;
      current.startX = event.clientX;
      current.origin = unband(x.get());
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDragging(true);
    }
    if (current.locked !== "x") return;
    current.samples.push([event.timeStamp, event.clientX]);
    if (current.samples.length > 16) current.samples.shift();
    if (stepping) return;
    x.set(band(current.origin + event.clientX - current.startX));
  }

  function finish(event: ReactPointerEvent<HTMLElement>, cancelled: boolean) {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    drag.current = null;
    const { step } = metrics.current;
    if (disabledRef.current) { if (current.locked === "x") setDragging(false); glide(target.current); return; }
    // Fidelity Médias (06/10/2026) : une annulation système (`pointercancel`) ne choisit RIEN — ni élan, ni quantisation de la
    // position, aucun `onSelect`. La piste revient à l'indice que le consommateur retient (le point engagé ; avec `onPass`, la
    // dernière carte qu'il a adoptée pendant le geste), qui redevient la cible. Aucun clic ne suit une annulation.
    if (cancelled) {
      if (current.locked === "x") setDragging(false);
      target.current = clamp(retained.current, 0, last);
      glide(target.current);
      return;
    }
    if (current.locked === "x") {
      swallowClick.current = true;
      setDragging(false);
      if (stepping) {
        const moved = event.clientX - current.startX;
        select(Math.abs(moved) <= REDUCED_STEP ? current.from : current.from + (moved < 0 ? 1 : -1));
        return;
      }
      const velocity = velocityOf(current.samples, event.timeStamp);
      // The landing slide comes from where the flick would carry the track, not from where the finger let go.
      let to = Math.round(-(x.get() + project(velocity)) / step);
      if (Math.abs(velocity) > FLICK) { const direction = velocity < 0 ? 1 : -1; if ((to - current.from) * direction < 1) to = current.from + direction; }
      // Inside the bounds the track moves 1:1 with the pointer, so the sampled release speed carries into the spring even when the
      // last move landed a frame early (the value's own velocity reads zero after 30ms). Past an edge, the banded value speed is the true one.
      const { min, max } = bounds(), shown = x.get();
      select(to, { velocity: shown >= min && shown <= max ? velocity : x.getVelocity() });
    } else if (current.caught) {
      swallowClick.current = true;
      select(Math.round(-x.get() / step));
    } else if (current.locked === null) {
      const hit = hitTest?.(event.clientX) ?? null;
      if (hit !== null && hit !== target.current) { swallowClick.current = true; select(hit); }
    }
  }

  return {
    x, progress, target, travel, drag, dragging, glide, select,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: (event) => finish(event, false),
      onPointerCancel: (event) => finish(event, true),
      onClickCapture: (event) => { if (swallowClick.current) { event.preventDefault(); event.stopPropagation(); swallowClick.current = false; } },
    },
  };
}
